import React, { useMemo } from "react"
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar"
import { formatCurrency, formatNumber } from "utils/tools"

class RevenueLineDetailsUI {
  static diffCellColor(amount: number): string {
    if (amount < 0) return 'var(--bs-danger-bg-subtle)' // yellow
    if (amount > 0) return 'var(--bs-success-bg-subtle)'
    return 'transparent'
  }

  static acutalTextColor(value: number): string {
    if (value > 0) return '' // green
    if (value < 0) return 'error' // red
    return ''
  }

  static textColor(value: number): string {
    if (value > 0) return 'success' // green
    if (value < 0) return 'error' // red
    return 'inherit'
  }

  static headerColor(
    cellFiscalYear: number,
    cellPeriod: number,
    nowFiscalYear: number,
    nowPeriod: number
  ): string | undefined {
    const cellKey = cellFiscalYear * 100 + cellPeriod
    const nowKey = nowFiscalYear * 100 + nowPeriod
    if (cellKey < nowKey) return 'var(--bs-past-period-bg)' // green (past periods)
  }
}

interface BudgetActualsTableProps {
  title?: string
  label?: string              // ⬅ NEW: left column label
  data: number[][]            // [actuals[], budgets[], diff[], planned[], forecast[], funnel[], consultingIncome[], accrued[]]
  nowFiscalYear: number
  nowPeriod: number
  fiscalYear: number
  showDetail: boolean
}

const BudgetActualsTable: React.FC<BudgetActualsTableProps> = ({
  title,
  label,
  data,
  nowFiscalYear,
  nowPeriod,
  fiscalYear,
  showDetail
}) => {
  const calendar = useMemo(() => new FiscalCalendar(DEFAULT_FISCAL_START), [])

  const budgetTotal = useMemo(() => {
    let total = 0;
    const [_actual, budgets] = data

    if (!budgets) return total;

    for (let i = 0; i < budgets.length; i++) {
      total += budgets[i]
    }

    return total
  }, [data])

  const actualForecastTotal = useMemo(() => {

    let total = 0
    const [actual, _budgets, _diff, planned, forecast] = data

    if (!actual || !planned) return total;
    for (let i = 0; i < actual.length; i++) {
      let val = actual[i];

      if (fiscalYear + ((i + 1) / 100) >= (nowFiscalYear + (nowPeriod / 100))) {
        val = forecast[i]
      }

      total += val;
    }

    return total
  }, [data, nowFiscalYear, nowPeriod])


  const forecastTotal = useMemo(() => {
    let total = 0
    const [, , , , forecast] = data

    if (!forecast) return total;
    for (let i = 0; i < forecast.length; i++) {
      total += forecast[i];
    }

    return total
  }, [data])

  const funnelTotal = useMemo(() => {
    let total = 0
    const [_actual, _budgets, _diff, _planned, _forecast, funnel] = data

    if (!funnel) return total;
    for (let i = 0; i < funnel.length; i++) {
      total += funnel[i];
    }

    return total
  }, [data])

  const consultingIncomeTotal = useMemo(() => {
    let total = 0
    const [, , , , , , consultingIncome] = data

    if (!consultingIncome) return total;
    for (let i = 0; i < consultingIncome.length; i++) {
      total += consultingIncome[i];
    }

    return total
  }, [data])

  const accruedTotal = useMemo(() => {
    let total = 0
    const [, , , , , , , accrued] = data

    if (!accrued) return total;
    for (let i = 0; i < accrued.length; i++) {
      total += accrued[i];
    }

    return total
  }, [data])



  if (!data || data.length !== 8) return null

  const [actuals, budgets, diff, planned, forecast, funnel, consultingIncome, accrued] = data
  // Add one more period for the total column
  const periods = Math.max(actuals.length, budgets.length, diff.length) + 1;


  return (
    <div className="budget-actuals-table">
      {title && <h5>{title}</h5>}

      <table className="table table-sm table-bordered budget-actuals-grid">
        <thead>
          <tr>
            <th>{label}</th>
            {Array.from({ length: periods }).map((_, i) => {
              const backgroundColor = RevenueLineDetailsUI.headerColor(
                fiscalYear,
                i + 1,
                nowFiscalYear,
                nowPeriod
              )

              if (i === periods - 1) {
                return <th key={i} className="text-end">
                  Total
                </th>
              }

              return <th key={i} className="text-end" style={{
                backgroundColor,
                color: backgroundColor ? 'white' : undefined
              }}>
                {calendar.periodToMonth(i + 1)}
              </th>
            })}
          </tr>
        </thead>

        <tbody>
          <tr>
            <td className="budget-row-label"><strong>Budget</strong></td>
            {Array.from({ length: periods }).map((_, i) => {
              if (i === periods - 1) {
                return <td key={i} className="budget-row-label text-end">
                  {formatCurrency(budgetTotal, "CAD", 0)}
                </td>
              }

              return <td key={i} className="text-end">
                {formatCurrency(Math.floor(budgets[i] ?? 0), "CAD", 0)}
              </td>
            })}
          </tr>
          <tr>
            <td className="budget-row-label"><strong>Actual / WIH</strong></td>
            {Array.from({ length: periods }).map((_, i) => {
              if (i === periods - 1) {
                return <td key={i} className={`text-end budget-row-label`} >
                  <span >
                    {formatCurrency(Math.floor(actualForecastTotal), "CAD", 0)}
                  </span>
                </td>
              }

              let value = actuals[i]
              if (fiscalYear + ((i + 1) / 100) >= (nowFiscalYear + (nowPeriod / 100))) {
                value = forecast[i]
              }

              return <td key={i} className={`text-end`}>
                <span className={RevenueLineDetailsUI.acutalTextColor(value)}>
                  {formatCurrency(Math.floor(value ?? 0), "CAD", 0)}
                </span>

              </td>
            }
            )}
          </tr>
          <tr>
            <td className="budget-row-label"><strong>Diff</strong></td>
            {Array.from({ length: periods }).map((_, i) => {

              if (i === periods - 1) {
                return <td
                  key={i}
                  className="text-end diff-cell"
                  style={{ backgroundColor: RevenueLineDetailsUI.diffCellColor(actualForecastTotal - budgetTotal) }}
                >
                  <span className={RevenueLineDetailsUI.textColor(actualForecastTotal - budgetTotal)}>
                    {formatCurrency(Math.floor(actualForecastTotal - budgetTotal), "CAD", 0)}
                  </span>
                </td>
              }
              const v = diff[i] ?? 0
              const cellColor = RevenueLineDetailsUI.diffCellColor(v);
              return (
                <td
                  key={i}
                  className="text-end diff-cell"
                  style={{ backgroundColor: cellColor }}
                >
                  <span className={RevenueLineDetailsUI.textColor(v)}>
                    {formatCurrency(Math.floor(v), 'CAD', 0)}
                  </span>
                </td>
              )
            })}
          </tr>
          {
            false &&
            <tr>
              <td className="budget-row-label"><strong>Work In Hand</strong></td>
              {Array.from({ length: periods }).map((_, i) => {
                if (i === periods - 1) {
                  return <td key={i} className={`text-end budget-row-label`} >
                    <span >
                      {formatCurrency(Math.floor(forecastTotal), "CAD", 0)}
                    </span>
                  </td>
                }

                let value = forecast[i]

                return <td key={i} className={`text-end`}>
                  <span className={RevenueLineDetailsUI.acutalTextColor(value)}>
                    {formatCurrency(Math.floor(value ?? 0), "CAD", 0)}
                  </span>

                </td>
              }
              )}
            </tr>
          }
          {
            false &&
            <tr>
              <td className="budget-row-label"><strong>Consulting Income</strong></td>
              {Array.from({ length: periods }).map((_, i) => {
                if (i === periods - 1) {
                  return <td key={i} className={`text-end budget-row-label`} >
                    <span >
                      {formatCurrency(Math.floor(consultingIncomeTotal), "CAD", 0)}
                    </span>
                  </td>
                }

                let value = consultingIncome[i]

                return <td key={i} className={`text-end`}>
                  <span className={RevenueLineDetailsUI.acutalTextColor(value)}>
                    {formatCurrency(Math.floor(value ?? 0), "CAD", 0)}
                  </span>

                </td>
              }
              )}
            </tr>
          }
          {
            false &&
            <tr>
              <td className="budget-row-label"><strong>Accrued</strong></td>
              {Array.from({ length: periods }).map((_, i) => {
                if (i === periods - 1) {
                  return <td key={i} className={`text-end budget-row-label`} >
                    <span >
                      {formatCurrency(Math.floor(accruedTotal), "CAD", 0)}
                    </span>
                  </td>
                }

                let value = accrued[i]

                return <td key={i} className={`text-end`}>
                  <span className={RevenueLineDetailsUI.acutalTextColor(value)}>
                    {formatCurrency(Math.floor(value ?? 0), "CAD", 0)}
                  </span>

                </td>
              }
              )}
            </tr>
          }
          {
            showDetail &&
            <tr>
              <td className="budget-row-label"><strong>Funnel</strong></td>
              {Array.from({ length: periods }).map((_, i) => {
                if (i === periods - 1) {
                  return <td key={i} className={`text-end budget-row-label`} >
                    <span >
                      {formatCurrency(Math.floor(funnelTotal), "CAD", 0)}
                    </span>
                  </td>
                }

                let value = funnel[i]

                return <td key={i} className={`text-end`}>
                  <span className={RevenueLineDetailsUI.acutalTextColor(value)}>
                    {formatCurrency(Math.floor(value ?? 0), "CAD", 0)}
                  </span>

                </td>
              }
              )}
            </tr>
          }
        </tbody>
      </table>
    </div>
  )
}

export default BudgetActualsTable
